import { useCallback, useMemo } from 'react';
import { useBearAuth } from '@bear-auth/react';
import { Popover, type TableColumnType } from 'antd';
import { useFela } from 'react-fela';
import { FormattedMessage, useIntl } from 'react-intl';

import { logger } from '@/config/logger';
import { AccessStatus, UserRole, type AdministrationUser } from '@/modules/api/codegen';
import { useConfirmationModal } from '@/modules/modals';
import { Button, Dropdown, DropdownButton, Icon } from '@/modules/ui';

import { useUpdateUsers } from '../../../hooks/useUpdateUsers';
import { ChangeStatusButton } from '../../ChangeStatusButton';
import { NameWithStatusTag } from '../../NameWithStatusTag';
import { useIsAdministrationTableRecordCurrentUser } from '../../UserAdministrationTable/hooks';
import * as felaRules from '../PeopleTabContent.rules';

export const usePeopleTableColumns = () => {
    const { css } = useFela();
    const { formatMessage } = useIntl();
    const { openConfirmationModal } = useConfirmationModal();
    const bearAuth = useBearAuth();
    const isCurrentUser = useIsAdministrationTableRecordCurrentUser();

    const updateUsersMutation = useUpdateUsers();

    const changeUserRole = useCallback(
        async (user: AdministrationUser, role: UserRole) => {
            const requestUserRoleChange = async () => {
                await updateUsersMutation.mutateAsync({
                    users: [user],
                    status: user.status!,
                    role,
                });

                if (isCurrentUser(user)) {
                    logger.info('Refetching auth info because the current user changed their role');

                    await bearAuth.actions.refetchAuthInfo?.();
                }
            };

            const isSelfDemotion = isCurrentUser(user) && user.role === UserRole.admin && role === UserRole.user;

            if (isSelfDemotion) {
                openConfirmationModal({
                    title: <FormattedMessage id='user-administration.table.actions.demoteSelf.confirmation.title' />,
                    content: <FormattedMessage id='user-administration.table.actions.demoteSelf.confirmation.content' />,
                    onConfirm: requestUserRoleChange,
                    confirmButtonText: formatMessage({ id: 'user-administration.table.actions.demoteSelf.confirmation.confirm' }),
                    cancelButtonText: formatMessage({ id: 'user-administration.table.actions.demoteSelf.confirmation.cancel' }),
                });

                return;
            }

            await requestUserRoleChange();
        },
        [updateUsersMutation, isCurrentUser, bearAuth, openConfirmationModal, formatMessage],
    );

    const changeUserStatus = useCallback(
        async (user: AdministrationUser, status: AccessStatus, showConfirmation: boolean = true) => {
            const localizationKey = status === AccessStatus.active ? 'grantAccess' : 'revokeAccess';

            const requestUserStatusChange = async () => {
                await updateUsersMutation.mutateAsync({
                    users: [user],
                    status,
                });
            };

            // TODO: Add the "do not show again" checkbox logic
            if (!showConfirmation) {
                await requestUserStatusChange();
                return;
            }

            openConfirmationModal({
                title: (
                    <FormattedMessage
                        id={`user-administration.table.actions.${localizationKey}.confirmation.title`}
                        values={{ user: user.name }}
                    />
                ),
                content: <FormattedMessage id={`user-administration.table.actions.${localizationKey}.confirmation.content`} />,
                onConfirm: async () => {
                    await requestUserStatusChange();
                },
                confirmButtonText: formatMessage({
                    id: `user-administration.table.actions.${localizationKey}.confirmation.confirm`,
                }),
                cancelButtonText: formatMessage({
                    id: `user-administration.table.actions.${localizationKey}.confirmation.cancel`,
                }),
            });
        },
        [openConfirmationModal, formatMessage, updateUsersMutation],
    );

    const columns = useMemo(
        () =>
            (
                [
                    {
                        dataIndex: 'name',
                        sorter: true,
                        render: (value, record) => <NameWithStatusTag name={value} status={record.status} />,
                    },
                    {
                        dataIndex: 'email',
                        sorter: true,
                    },
                    {
                        dataIndex: 'group',
                        render(_, record) {
                            if (record.groups && record.groups.length > 0) {
                                if (record.groups.length === 1) {
                                    return record.groups[0].name;
                                }

                                return (
                                    <Popover
                                        content={record.groups.map(group => (
                                            <div key={group.id}>{group.name}</div>
                                        ))}
                                        arrow={false}
                                        placement='bottom'
                                    >
                                        <DropdownButton>{record.groups.length}</DropdownButton>
                                    </Popover>
                                );
                            }
                        },
                    },
                    {
                        dataIndex: 'role',
                        render(_, record) {
                            if (!record.role) return null;

                            return (
                                <Dropdown
                                    items={[UserRole.admin, UserRole.user].map(role => ({
                                        key: role,
                                        label: <FormattedMessage id={`user-administration.role.${role}`} />,
                                        active: record.role === role,
                                        onClick: () => changeUserRole(record, role),
                                    }))}
                                >
                                    <FormattedMessage id={`user-administration.role.${record.role}`} />
                                </Dropdown>
                            );
                        },
                    },
                    {
                        key: 'actions',
                        render(_, record) {
                            if (isCurrentUser(record)) {
                                return null;
                            }

                            if (record.status === AccessStatus.pending) {
                                return (
                                    <div className={css(felaRules.actionsContainer)}>
                                        <Button
                                            type='text'
                                            icon={<Icon type='checkmark' />}
                                            onClick={() => changeUserStatus(record, AccessStatus.active)}
                                        >
                                            <FormattedMessage id='user-administration.table.actions.acceptRequest' />
                                        </Button>
                                        <Button
                                            type='text'
                                            icon={<Icon type='close' />}
                                            color='inputsError'
                                            onClick={() => changeUserStatus(record, AccessStatus.inactive, false)}
                                        >
                                            <FormattedMessage id='user-administration.table.actions.rejectRequest' />
                                        </Button>
                                    </div>
                                );
                            }

                            const newStatus = record.status === AccessStatus.active ? AccessStatus.inactive : AccessStatus.active;

                            return (
                                <div className={css(felaRules.actionsContainer)}>
                                    <ChangeStatusButton
                                        newStatus={newStatus}
                                        onClick={() => {
                                            newStatus === AccessStatus.active
                                                ? changeUserStatus(record, newStatus, true)
                                                : changeUserStatus(record, newStatus, false);
                                        }}
                                    />
                                </div>
                            );
                        },
                    },
                ] as const satisfies TableColumnType<AdministrationUser>[]
            ).map(column => ({
                ...column,
                title: 'dataIndex' in column ? formatMessage({ id: `user-administration.table.columns.${column.dataIndex}` }) : undefined,
            })),
        [css, formatMessage, changeUserRole, changeUserStatus, isCurrentUser],
    );

    return columns;
};
